import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';

import { useGetAllCards } from '../../hooks/apolloHooks/useGetAllCards';
import { useGetCustomer } from '../../hooks/apolloHooks/useGetCustomer';
import { GET_BANKCARDS_CUSTOMER_DATA_QUERY, GET_BASIC_CUSTOMER_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { BankCardsCustomerDataResponse, BasicCustomerDataResponse, UnifiedCard } from '../../integrations/allkort/graphql/interface/Customer';
import { filterCards, mergeCardsData } from '../../utils/cardMergeUtils';
import CardItem from '../elements/cards/CardItem';
import CardsFilter, { CardsFilterState } from '../elements/cards/CardsFilter';
import CardsViewStyle, { CardsViewStyleValue } from '../elements/cards/CardsViewStyle';
import FetchingAnimation from '../elements/FetchingAnimation';
import Link from '../elements/Link';
import RightsWrapper from '../elements/RightsWrapper';
import { ExternalLinkIcon } from '../svgs/DashboardSVGs';
import { AddCardIcon, EqualizerIcon, InfoIcon, InfoIconOutlined, PlusIcon } from '../svgs/ExpressonSVGs';
import ListCardsView from '../views/Cards/ListCardsView';
import OrderNewCard from '../views/Cards/OrderNewCard';

type Props = {}
const CARDS_VIEW_STYLE_MIN_WIDTH = 1000;

const CardsPage: React.FC<Props> = () => {

    // State for tracking active card and filters
    const [activeCardId, setActiveCardId] = useState<string | null>(null)
    const [mergedCards, setMergedCards] = useState<UnifiedCard[]>([])
    const [filteredCards, setFilteredCards] = useState<UnifiedCard[]>([])
    const [filterState, setFilterState] = useState<CardsFilterState>({
        searchTerm: '',
        typeFilters: [],
        includeDisabled: false
    })
    const [isOrderCardClicked, setIsOrderCardClicked] = useState(false)
    const [isOrderNewCardOpen, setIsOrderNewCardOpen] = useState(false)
    const [viewStyle, setViewStyle] = useState<CardsViewStyleValue>('grid')
    const [isCardsViewStyleAvailable, setIsCardsViewStyleAvailable] = useState(
        () => window.matchMedia(`(min-width: ${CARDS_VIEW_STYLE_MIN_WIDTH}px)`).matches
    )

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(min-width: ${CARDS_VIEW_STYLE_MIN_WIDTH}px)`)
        const handleMediaQueryChange = (event: MediaQueryListEvent) => {
            setIsCardsViewStyleAvailable(event.matches)
            if (!event.matches) {
                setViewStyle('grid')
            }
        }

        setIsCardsViewStyleAvailable(mediaQuery.matches)
        mediaQuery.addEventListener('change', handleMediaQueryChange)

        return () => mediaQuery.removeEventListener('change', handleMediaQueryChange)
    }, [])

    const { data: customerBasicData, loading: customerLoading, error: customerError } = useGetCustomer<BasicCustomerDataResponse>(
        GET_BASIC_CUSTOMER_DATA_QUERY,
        undefined
    );

    // Fetch ALL cards with automatic pagination (up to 200 cards)
    const { cards: allRegularCards, loading: cardsLoading, error: cardsError, refetch: refetchCards } = useGetAllCards();

    // Fetch bank cards data with same pagination as regular cards
    const bankCardsVariables = useMemo(() => ({ first: null, after: null, before: null, last: 49 }), []);

    const { data: bankCardsData, loading: bankCardsLoading, error: bankCardsError } = useGetCustomer<BankCardsCustomerDataResponse>(
        GET_BANKCARDS_CUSTOMER_DATA_QUERY,
        bankCardsVariables,
        { fetchPolicy: 'network-only' }
    );

    if (customerError) console.error('Failed to load customer data:', customerError.message)
    if (cardsError) console.error('Failed to load cards:', cardsError.message)
    if (bankCardsError) console.error('Failed to load bank cards:', bankCardsError.message)

    // Get flag for filtering
    const isCompany = customerBasicData?.currentCustomer?.isCompany || false

    // Merge cards when data is available
    useEffect(() => {
        //console.log('[CardsPage] merge effect — allRegularCards:', allRegularCards.length, 'bankCardsData:', !!bankCardsData, 'cardsLoading:', cardsLoading);
        if (allRegularCards.length > 0 || bankCardsData) {
            const bankCards = bankCardsData?.currentCustomer?.bankCards?.nodes || []
            const merged = mergeCardsData(allRegularCards, bankCards)
            //console.log('[CardsPage] merged:', merged.length, 'cards (regular:', allRegularCards.length, ', bank:', bankCards.length, ')');
            setMergedCards(merged)
            // Apply current filters to newly merged cards
            const filtered = filterCards(
                merged,
                filterState.searchTerm,
                filterState.typeFilters,
                filterState.includeDisabled,
                isCompany
            )

            setFilteredCards(filtered)
        }
    }, [allRegularCards, bankCardsData, isCompany])

    // Apply filters when filter state changes
    useEffect(() => {
        const filtered = filterCards(
            mergedCards,
            filterState.searchTerm,
            filterState.typeFilters,
            filterState.includeDisabled,
            isCompany
        )
        setFilteredCards(filtered)
    }, [filterState, mergedCards, isCompany])

    // Handle filter changes
    const handleFilterChange = (newFilterState: CardsFilterState) => {
        setFilterState(newFilterState)
    }

    // Handle card click
    const handleCardClick = (cardId: string) => {
        // Toggle the active state: if already active, deactivate; otherwise activate
        if (activeCardId === cardId) {
            setActiveCardId(null)
        } else {
            setActiveCardId(cardId)
        }
    }

    // Handle card update (name, pin, etc.) - refetch to get updated data
    const handleCardUpdate = () => {
        refetchCards()
    }

    // Handle order card click for mobile
    const handleOrderCardClick = () => {
        if (window.innerWidth < 768) {
            setIsOrderCardClicked(!isOrderCardClicked)
        }
    }

    // Check card types from all fetched cards
    const hasSmartCard = allRegularCards.some((card: any) => card.type?.shortName === 'P')
    const hasCreditCard = allRegularCards.some((card: any) => card.type?.shortName === 'K')

    const applyForCardsurl = isCompany
        ? import.meta.env.VITE_VERIFIED_URL_COMPANY || 'https://app.verified.eu/web/qstar-card-company/'
        : import.meta.env.VITE_VERIFIED_URL_PRIVATE || 'https://app.verified.eu/web/qstar-card-private/'

    const cardsContentLoading = customerLoading || cardsLoading || bankCardsLoading

    return (
        <div className="Section cards-page-component">
            <div className="Row Row--JustifyLeft">
                <div className="Row__Inner shadow-one white-pale-section">
                    <div className="cards-overview">
                        <div className="cards-overview__top-section">
                            <div className="cards-overview__header">
                                <h1>Mina kort</h1>
                                <p>Här kan du enkelt administrera dina kort. Klicka på kortet för att göra ändringar.</p>
                                <div className="cards-filters">
                                    <CardsFilter
                                        isCompany={isCompany}
                                        onFilterChange={handleFilterChange}
                                    />
                                </div>
                            </div>

                            <div className="cards-overview__order-card">
                                <div className={classNames('cards-overview__order-new', {
                                    'clicked': isOrderCardClicked
                                })}
                                    onClick={handleOrderCardClick}>
                                    <h2>
                                        Kortbeställning
                                        <AddCardIcon />
                                    </h2>
                                    {(hasSmartCard || hasCreditCard) ? (
                                        <div className="cards-overview__has-active-cards">
                                            <p>
                                                Du som har {hasSmartCard && 'ett aktivt smartkort '}{(hasSmartCard && hasCreditCard) && 'eller '} {hasCreditCard && 'ett aktivt tankkort '}
                                                kan beställa nya kort här.
                                            </p>
                                            <RightsWrapper
                                                requiredRight="mutation.currentCustomer.addCustomerTask"
                                                disableButtonText={'Beställ'}
                                                disableButtonIcon={<PlusIcon width={12} height={12} />}
                                                disableButtonClasses="body-text"
                                            >
                                                <button
                                                    className="button-edit body-text"
                                                    aria-label={'Beställ qstarkort'}
                                                    type="button"
                                                    onClick={() => setIsOrderNewCardOpen(true)}
                                                >
                                                    <PlusIcon width={12} height={12} />
                                                    {'Beställ kort'}
                                                </button>
                                            </RightsWrapper>
                                            <OrderNewCard
                                                orderReplacement={false}
                                                isOpen={isOrderNewCardOpen}
                                                isCompany={isCompany}
                                                hasK={hasCreditCard}
                                                hasP={hasSmartCard}
                                                onClose={() => setIsOrderNewCardOpen(false)}
                                                onSuccess={refetchCards}
                                            />

                                        </div>
                                    ) : (
                                        <div className="cards-overview__no-active-cards">
                                            <p>
                                                Du har inga smartkort eller {isCompany ? 'företagskort' : 'tankkort'}.
                                                Klicka på länken nedan för att göra en kortansökan.
                                            </p>
                                            <Link
                                                className="dashboard-button"
                                                ariaLabel={'Beställ kort via Verified'}
                                                href={applyForCardsurl}
                                                openNewTab={true}
                                            >
                                                {'Gör en kortansökan'}
                                                <ExternalLinkIcon />
                                            </Link>
                                        </div>
                                    )}
                                </div>
                                <p className="info-text-mobile">
                                    <InfoIcon />
                                    Klicka på ett kort nedan för att administrera det.
                                </p>
                            </div>
                        </div>
                        <div className="cards-meta-info">
                            <p>
                                {filteredCards.length} {filterState.includeDisabled
                                    ? (filteredCards.length === 1 ? 'spärrat kort' : 'spärrade kort')
                                    : (filteredCards.length === 1 ? 'aktivt kort' : 'aktiva kort')
                                }.
                            </p>

                            {isCardsViewStyleAvailable && (
                                <CardsViewStyle
                                    viewStyle={viewStyle}
                                    onViewStyleChange={setViewStyle}
                                />
                            )}
                        </div>

                        <div className="cards-listing">
                            {viewStyle === 'list' ? (
                                <ListCardsView
                                    filteredCards={filteredCards}
                                    cardsContentLoading={cardsContentLoading}
                                    filterState={filterState}
                                    applyForCardsurl={applyForCardsurl}
                                    isCompany={isCompany}
                                    onCardUpdate={handleCardUpdate}
                                />
                            ) : cardsContentLoading ? (
                                <FetchingAnimation width={1320} maxWidth={true} height={500} marginBottom={0} marginTop={0} />
                            ) : filteredCards.length > 0 ? (
                                <div className="cards-grid">
                                    {filteredCards.map((card) => (
                                        <CardItem
                                            key={card.id}
                                            card={card}
                                            isActive={activeCardId === card.id}
                                            isCompany={isCompany}
                                            onCardClick={handleCardClick}
                                            onCardUpdate={handleCardUpdate}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="cards-empty">
                                    {filterState.searchTerm.trim() || filterState.typeFilters.length > 0 || filterState.includeDisabled ? (
                                        <p>
                                            <EqualizerIcon width={28} height={28} />
                                            Ingen träff på nuvarande filtrering eller sökning
                                        </p>
                                    ) : (
                                        <p>
                                            <InfoIconOutlined width={28} height={28} />
                                            Du har för närvarande inga aktiva kort, ansök via{' '}
                                            <a href={applyForCardsurl} target="_blank" rel="noopener noreferrer">
                                                Verified
                                            </a>
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CardsPage
